import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import RswEditor, { EditorProvider } from "react-simple-wysiwyg";
import LoadingButton from "../../components/LoadingButton";
import { hostname, isSafeUrl } from "../../utils";
import LogoUploader from "./LogoUploader";
import WhitelabelAgreementDialog from "./dialogs/WhitelabelAgreementDialog";

interface WhitelabelSettings {
  title: string;
  headTitle: string;
  faviconText: string;
  // Light mode colors
  primaryColor: string;
  secondaryColor: string;
  // Dark mode colors (optional - falls back to light mode colors if not set)
  primaryColorDark: string;
  secondaryColorDark: string;
  isMainSite: boolean;
}

export interface WhitelabelInstance {
  id: string;
  domain?: string;
  settings: any;
  termsUrl?: string;
  privacyUrl?: string;
  salesEmail?: string;
  securityEmail?: string;
  supportUrl?: string;
  pricingUrl?: string;
  documentationUrl?: string;
  customSubscriptionEnabled?: boolean;
  disableFreeAccounts?: boolean;
  disableEnterpriseAccounts?: boolean;
  companyProvisioningEnabled?: boolean;
  rootPage?: string; // 'login' | 'home' - controls which page is shown at root URL
  onboardingTeamSelection?: boolean; // Enable team selection during onboarding
  defaultPublicEmployeeIds?: string[]; // Default public employees to add to new companies
  headingFontFamily?: string; // Font family for headings
  bodyFontFamily?: string; // Font family for body text
  customFontUrl?: string; // Custom Google Fonts URL
  is_owner: boolean;
  created_date?: string;
  updated_date?: string;
  newCompanyWebhook: string;
  newCompanyListId: string;
  userImagePrompt?: string;
  userImageModel?: string;
  employeeTermSingular?: string;
  employeeTermPlural?: string;
  // SMTP settings
  notificationsEmail?: string;
  smtpEnabled?: boolean;
  smtpHost?: string;
  smtpPort?: number;
  smtpUsername?: string;
  smtpPassword?: string;
  smtpUseTls?: boolean;
  smtpUseSsl?: boolean;
  // Welcome email customization
  welcomeEmailEnabled?: boolean;
  welcomeEmailSubject?: string;
  welcomeEmailContent?: string;
  // Onboarding completion customization
  onboardingCompletionContent?: string;
  logos?: {
    horizontal_logo?: {
      id: string;
      url: string;
      publicUrl: string;
    };
    horizontal_logo_dark?: {
      id: string;
      url: string;
      publicUrl: string;
    };
    square_logo?: {
      id: string;
      url: string;
      publicUrl: string;
    };
    favicon?: {
      id: string;
      url: string;
      publicUrl: string;
    };
    onboarding_hero?: {
      id: string;
      url: string;
      publicUrl: string;
    };
  };
}

export default function WhitelabelEditorDialog({
  open,
  onClose,
  getToken,
  mode,
  whitelabel,
  eventListener,
}: {
  open: boolean;
  onClose: () => void;
  getToken: Function;
  mode: string;
  whitelabel?: WhitelabelInstance | null;
  eventListener?: Function;
}) {
  const defaultSettings: WhitelabelSettings = {
    title: "GTM AI",
    headTitle: "GTM AI | Train AI on Your Business",
    faviconText: "P",
    // Light mode colors
    primaryColor: "rgb(22, 155, 232)",
    secondaryColor: "rgb(35, 55, 132)",
    // Dark mode colors
    primaryColorDark: "#A3F7BF",
    secondaryColorDark: "#ffffff",
    isMainSite: false,
  };

  const [settings, setSettings] = useState<WhitelabelSettings>(defaultSettings);
  const [originalSettings, setOriginalSettings] =
    useState<WhitelabelSettings>(defaultSettings);
  const [domain, setDomain] = useState("");
  const [termsUrl, setTermsUrl] = useState("");
  const [privacyUrl, setPrivacyUrl] = useState("");
  const [salesEmail, setSalesEmail] = useState("");
  const [securityEmail, setSecurityEmail] = useState("");
  const [supportUrl, setSupportUrl] = useState("");
  const [pricingUrl, setPricingUrl] = useState("");
  const [documentationUrl, setDocumentationUrl] = useState("");
  const [newCompanyWebhook, setNewCompanyWebhook] = useState("");
  const [newCompanyListId, setNewCompanyListId] = useState("");
  const [userImagePrompt, setUserImagePrompt] = useState("");
  const [userImageModel, setUserImageModel] = useState("");
  const [imageModels, setImageModels] = useState<Array<any>>([]);
  const [publicEmployeeFilter, setPublicEmployeeFilter] = useState("all");
  const [publicFolderFilter, setPublicFolderFilter] = useState("all");
  const [showEmployeeTemplates, setShowEmployeeTemplates] = useState(false);
  const [isOwner, setIsOwner] = useState(true);

  // Terminology customization state
  const [employeeTermSingular, setEmployeeTermSingular] = useState("");
  const [employeeTermPlural, setEmployeeTermPlural] = useState("");

  // Subscription settings state
  const [customSubscriptionEnabled, setCustomSubscriptionEnabled] =
    useState(false);
  const [disableFreeAccounts, setDisableFreeAccounts] = useState(false);
  const [disableEnterpriseAccounts, setDisableEnterpriseAccounts] =
    useState(false);

  // Root page configuration
  const [rootPage, setRootPage] = useState<string>("login");

  // Typography settings state
  const [headingFontFamily, setHeadingFontFamily] = useState("");
  const [bodyFontFamily, setBodyFontFamily] = useState("");

  // Google Fonts from API
  interface GoogleFont {
    family: string;
    category: string;
    variants: string[];
    subsets: string[];
  }
  const [googleFonts, setGoogleFonts] = useState<GoogleFont[]>([]);
  const [fontsLoading, setFontsLoading] = useState(false);

  // Build font options from Google Fonts API data
  const FONT_OPTIONS = [
    { value: "", label: "Default", category: "" },
    ...googleFonts.map((font) => ({
      value: font.family,
      label: `${font.family} (${font.category})`,
      category: font.category,
    })),
  ];

  // SMTP settings state
  const [smtpEnabled, setSmtpEnabled] = useState(false);
  const [notificationsEmail, setNotificationsEmail] = useState("");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState<number | "">(587);
  const [smtpUsername, setSmtpUsername] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpUseTls, setSmtpUseTls] = useState(true);
  const [smtpUseSsl, setSmtpUseSsl] = useState(false);

  // Welcome email customization state
  const [welcomeEmailEnabled, setWelcomeEmailEnabled] = useState(true);
  const [welcomeEmailSubject, setWelcomeEmailSubject] = useState("");
  const [welcomeEmailContent, setWelcomeEmailContent] = useState("");

  // Onboarding completion customization state
  const [onboardingCompletionContent, setOnboardingCompletionContent] = useState("");

  // Onboarding team selection state
  const [onboardingTeamSelection, setOnboardingTeamSelection] = useState(true);

  // Default public employees state
  interface PublicEmployee {
    id: string;
    name: string;
    title: string;
    picture?: string;
    ownerCompany?: {
      id: string;
      name: string;
      logoUrl?: string;
    };
  }
  const [defaultPublicEmployeeIds, setDefaultPublicEmployeeIds] = useState<string[]>([]);
  const [selectedDefaultEmployees, setSelectedDefaultEmployees] = useState<PublicEmployee[]>([]);
  const [availablePublicEmployees, setAvailablePublicEmployees] = useState<PublicEmployee[]>([]);
  const [publicEmployeesLoading, setPublicEmployeesLoading] = useState(false);
  const [publicEmployeeSearch, setPublicEmployeeSearch] = useState("");
  const [publicEmployeesPage, setPublicEmployeesPage] = useState(1);
  const [publicEmployeesHasMore, setPublicEmployeesHasMore] = useState(true);
  const [publicEmployeesTotal, setPublicEmployeesTotal] = useState(0);

  // Default prompt constant
  const DEFAULT_IMAGE_PROMPT =
    "Professional business headshot of one employee named {{name}} who has the job role of {{title}} at a company named {{companyName}}, 4k, high-end corporate photography style, clean and minimal, subtle smile, professional DSLR quality, sharp focus on face, depth of field, full color, LinkedIn profile photo aesthetic, ultra-realistic, photorealistic --ar 3:4 --s 750";
  // Default image model constant (Leonardo Kino XL - matches employee photo service default)
  const DEFAULT_IMAGE_MODEL = "aa77f04e-3eec-4034-9c07-d0f619684628";
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Logo state
  const [horizontalLogoId, setHorizontalLogoId] = useState<string | null>(null);
  const [horizontalLogoUrl, setHorizontalLogoUrl] = useState<string | null>(
    null
  );
  const [horizontalLogoDarkId, setHorizontalLogoDarkId] = useState<
    string | null
  >(null);
  const [horizontalLogoDarkUrl, setHorizontalLogoDarkUrl] = useState<
    string | null
  >(null);
  const [squareLogoId, setSquareLogoId] = useState<string | null>(null);
  const [squareLogoUrl, setSquareLogoUrl] = useState<string | null>(null);
  const [faviconId, setFaviconId] = useState<string | null>(null);
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  const [onboardingHeroId, setOnboardingHeroId] = useState<string | null>(null);
  const [onboardingHeroUrl, setOnboardingHeroUrl] = useState<string | null>(
    null
  );

  // Stripe Connect state
  const [stripeStatus, setStripeStatus] = useState<any>(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  // SMTP test state
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Agreement dialog state
  const [agreementDialogOpen, setAgreementDialogOpen] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);

  const getImageModels = async () => {
    const currentToken = await getToken();
    try {
      const response = await axios.get(`${hostname}/api/v0/images/models`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      setImageModels(response.data);
    } catch (error) {
      console.error("Error fetching image models:", error);
    }
  };

  // Fetch Google Fonts from API
  const getGoogleFonts = async () => {
    if (googleFonts.length > 0) return; // Already loaded
    setFontsLoading(true);
    try {
      const currentToken = await getToken();
      const response = await axios.get(`${hostname}/whitelabel/google-fonts`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      if (response.data.success && response.data.fonts) {
        setGoogleFonts(response.data.fonts);
      }
    } catch (error) {
      console.error("Error fetching Google Fonts:", error);
    } finally {
      setFontsLoading(false);
    }
  };

  // Fetch public employees for selection with pagination
  const fetchPublicEmployees = async (search?: string, page: number = 1, append: boolean = false) => {
    setPublicEmployeesLoading(true);
    try {
      const currentToken = await getToken();
      const params = new URLSearchParams();
      params.append("per_page", "20");
      params.append("page", String(page));
      if (search) {
        params.append("search", search);
      }
      const response = await axios.get(
        `${hostname}/public-employees?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );
      if (response.data.success) {
        const newEmployees = response.data.employees || [];
        const pagination = response.data.pagination || {};

        if (append) {
          setAvailablePublicEmployees(prev => [...prev, ...newEmployees]);
        } else {
          setAvailablePublicEmployees(newEmployees);
        }

        setPublicEmployeesPage(page);
        setPublicEmployeesTotal(pagination.total || newEmployees.length);
        setPublicEmployeesHasMore(pagination.page < pagination.totalPages);
      }
    } catch (error) {
      console.error("Error fetching public employees:", error);
    } finally {
      setPublicEmployeesLoading(false);
    }
  };

  // Load more public employees when scrolling
  const handleLoadMorePublicEmployees = () => {
    if (!publicEmployeesLoading && publicEmployeesHasMore) {
      fetchPublicEmployees(publicEmployeeSearch, publicEmployeesPage + 1, true);
    }
  };

  // Load selected default employees by IDs
  const loadSelectedDefaultEmployees = async (employeeIds: string[]) => {
    if (!employeeIds || employeeIds.length === 0) {
      setSelectedDefaultEmployees([]);
      return;
    }
    try {
      const currentToken = await getToken();
      // Fetch all public employees and filter by IDs
      const response = await axios.get(
        `${hostname}/public-employees?per_page=100`,
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );
      if (response.data.success) {
        const allEmployees = response.data.employees || [];
        const selected = allEmployees.filter((emp: PublicEmployee) =>
          employeeIds.includes(emp.id)
        );
        setSelectedDefaultEmployees(selected);
      }
    } catch (error) {
      console.error("Error loading selected default employees:", error);
    }
  };

  useEffect(() => {
    if (open) {
      getImageModels();
      getGoogleFonts();
      fetchPublicEmployees();
    }
  }, [open]);

  // Set default model when models are loaded and no model is set
  // Default to Leonardo Kino XL (same as employee photo service)
  useEffect(() => {
    if (imageModels.length > 0 && !userImageModel) {
      setUserImageModel(DEFAULT_IMAGE_MODEL);
    }
  }, [imageModels]);

  // Load fonts for preview in the editor
  useEffect(() => {
    const fontsToLoad: string[] = [];
    if (headingFontFamily) fontsToLoad.push(headingFontFamily);
    if (bodyFontFamily) fontsToLoad.push(bodyFontFamily);

    if (fontsToLoad.length > 0) {
      const uniqueFonts = [...new Set(fontsToLoad)];
      const fontQuery = uniqueFonts
        .map((font) => font.replace(/ /g, "+") + ":wght@400;500;600;700")
        .join("&family=");
      const fontUrl = `https://fonts.googleapis.com/css2?family=${fontQuery}&display=swap`;

      const existingLinks = document.querySelectorAll('link[rel="stylesheet"]');
      let linkExists = false;
      existingLinks.forEach((link) => {
        if ((link as HTMLLinkElement).href === fontUrl) {
          linkExists = true;
        }
      });
      if (!linkExists) {
        const link = document.createElement("link");
        link.href = fontUrl;
        link.rel = "stylesheet";
        document.head.appendChild(link);
      }
    }
  }, [headingFontFamily, bodyFontFamily]);

  useEffect(() => {
    if (open && whitelabel) {
      // Edit mode - load existing settings
      setSettings(whitelabel.settings || defaultSettings);
      setOriginalSettings(whitelabel.settings || defaultSettings);
      setDomain(whitelabel.domain || "");
      setTermsUrl(whitelabel.termsUrl || "");
      setPrivacyUrl(whitelabel.privacyUrl || "");
      setSalesEmail(whitelabel.salesEmail || "");
      setSecurityEmail(whitelabel.securityEmail || "");
      setSupportUrl(whitelabel.supportUrl || "");
      setPricingUrl(whitelabel.pricingUrl || "");
      setDocumentationUrl(whitelabel.documentationUrl || "");
      setNewCompanyWebhook(whitelabel.newCompanyWebhook || "");
      setNewCompanyListId(whitelabel.newCompanyListId || "");
      // Only use default if no custom prompt exists from backend
      if (whitelabel.userImagePrompt) {
        setUserImagePrompt(whitelabel.userImagePrompt);
      } else {
        setUserImagePrompt(DEFAULT_IMAGE_PROMPT);
      }
      // Set model from whitelabel if available
      if (whitelabel.userImageModel) {
        setUserImageModel(whitelabel.userImageModel);
      }
      setPublicEmployeeFilter(
        whitelabel.settings?.publicEmployeeFilter || "all"
      );
      setPublicFolderFilter(whitelabel.settings?.publicFolderFilter || "all");
      setShowEmployeeTemplates(whitelabel.settings?.showEmployeeTemplates || false);
      setIsOwner(whitelabel.is_owner);

      // Load terminology settings
      setEmployeeTermSingular(whitelabel.employeeTermSingular || "");
      setEmployeeTermPlural(whitelabel.employeeTermPlural || "");

      // Load subscription settings
      setCustomSubscriptionEnabled(
        whitelabel.customSubscriptionEnabled || false
      );
      setDisableFreeAccounts(whitelabel.disableFreeAccounts || false);
      setDisableEnterpriseAccounts(
        whitelabel.disableEnterpriseAccounts || false
      );
      setRootPage(whitelabel.rootPage || "login");

      // Load typography settings
      setHeadingFontFamily(whitelabel.headingFontFamily || "");
      setBodyFontFamily(whitelabel.bodyFontFamily || "");

      // Load SMTP settings
      setSmtpEnabled(whitelabel.smtpEnabled || false);
      setNotificationsEmail(whitelabel.notificationsEmail || "");
      setSmtpHost(whitelabel.smtpHost || "");
      setSmtpPort(whitelabel.smtpPort || 587);
      setSmtpUsername(whitelabel.smtpUsername || "");
      setSmtpPassword(whitelabel.smtpPassword || "");
      setSmtpUseTls(whitelabel.smtpUseTls !== false);
      setSmtpUseSsl(whitelabel.smtpUseSsl || false);

      // Load welcome email customization settings
      setWelcomeEmailEnabled(whitelabel.welcomeEmailEnabled !== false);
      setWelcomeEmailSubject(whitelabel.welcomeEmailSubject || "");
      setWelcomeEmailContent(whitelabel.welcomeEmailContent || "");

      // Load onboarding completion customization settings
      setOnboardingCompletionContent(whitelabel.onboardingCompletionContent || "");

      // Load onboarding team selection setting
      setOnboardingTeamSelection(whitelabel.onboardingTeamSelection ?? true);

      // Load default public employees
      const employeeIds = whitelabel.defaultPublicEmployeeIds || [];
      setDefaultPublicEmployeeIds(employeeIds);
      loadSelectedDefaultEmployees(employeeIds);

      // Set logo state
      setHorizontalLogoId(whitelabel.logos?.horizontal_logo?.id || null);
      setHorizontalLogoUrl(
        whitelabel.logos?.horizontal_logo?.publicUrl || null
      );
      setHorizontalLogoDarkId(
        whitelabel.logos?.horizontal_logo_dark?.id || null
      );
      setHorizontalLogoDarkUrl(
        whitelabel.logos?.horizontal_logo_dark?.publicUrl || null
      );

      setSquareLogoId(whitelabel.logos?.square_logo?.id || null);
      setSquareLogoUrl(whitelabel.logos?.square_logo?.publicUrl || null);

      setFaviconId(whitelabel.logos?.favicon?.id || null);
      setFaviconUrl(whitelabel.logos?.favicon?.publicUrl || null);

      setOnboardingHeroId(whitelabel.logos?.onboarding_hero?.id || null);
      setOnboardingHeroUrl(
        whitelabel.logos?.onboarding_hero?.publicUrl || null
      );
    } else if (open && !whitelabel) {
      // Create mode - use defaults
      setSettings(defaultSettings);
      setOriginalSettings(defaultSettings);
      setDomain("");
      setTermsUrl("");
      setPrivacyUrl("");
      setSalesEmail("");
      setSecurityEmail("");
      setSupportUrl("");
      setPricingUrl("");
      setDocumentationUrl("");
      setNewCompanyWebhook("");
      setNewCompanyListId("");
      setUserImagePrompt(DEFAULT_IMAGE_PROMPT);
      setUserImageModel(DEFAULT_IMAGE_MODEL);
      setPublicEmployeeFilter("all");
      setPublicFolderFilter("all");
      setShowEmployeeTemplates(false);
      setIsOwner(true);

      // Reset terminology settings
      setEmployeeTermSingular("");
      setEmployeeTermPlural("");

      // Reset typography settings
      setHeadingFontFamily("");
      setBodyFontFamily("");

      // Reset SMTP settings
      setSmtpEnabled(false);
      setNotificationsEmail("");
      setSmtpHost("");
      setSmtpPort(587);
      setSmtpUsername("");
      setSmtpPassword("");
      setSmtpUseTls(true);
      setSmtpUseSsl(false);

      // Reset welcome email customization settings
      setWelcomeEmailEnabled(true);
      setWelcomeEmailSubject("");
      setWelcomeEmailContent("");

      // Reset onboarding completion customization settings
      setOnboardingCompletionContent("");

      // Reset onboarding team selection
      setOnboardingTeamSelection(true);

      // Reset logo state
      setHorizontalLogoId(null);
      setHorizontalLogoUrl(null);
      setHorizontalLogoDarkId(null);
      setHorizontalLogoDarkUrl(null);
      setSquareLogoId(null);
      setSquareLogoUrl(null);
      setFaviconId(null);
      setFaviconUrl(null);
      setOnboardingHeroId(null);
      setOnboardingHeroUrl(null);
    }
  }, [open, whitelabel]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSave = async () => {
    // If creating new whitelabel and agreement not yet accepted, show agreement dialog
    if (!whitelabel && !agreementAccepted) {
      setAgreementDialogOpen(true);
      return;
    }

    // Validate sales email if provided
    if (salesEmail && !validateEmail(salesEmail)) {
      setSnackbarMessage("Please enter a valid sales email address");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setSaving(true);
    try {
      const currentToken = await getToken();

      // Debug: Log current logo state
      console.log("Logo state before save:", {
        horizontalLogoId,
        horizontalLogoUrl,
        squareLogoId,
        squareLogoUrl,
        faviconId,
        faviconUrl,
        onboardingHeroId,
        onboardingHeroUrl,
      });

      if (whitelabel) {
        // Edit existing whitelabel
        await axios.put(
          `${hostname}/whitelabel/${whitelabel.id}`,
          {
            domain,
            settings: {
              ...settings,
              publicEmployeeFilter,
              publicFolderFilter,
              showEmployeeTemplates,
            },
            termsUrl: termsUrl || null,
            privacyUrl: privacyUrl || null,
            salesEmail: salesEmail || null,
            securityEmail: securityEmail || null,
            supportUrl: supportUrl || null,
            pricingUrl: pricingUrl || null,
            documentationUrl: documentationUrl || null,
            newCompanyWebhook: newCompanyWebhook || null,
            newCompanyListId: newCompanyListId || null,
            userImagePrompt: userImagePrompt || null,
            userImageModel: userImageModel || null,
            employeeTermSingular: employeeTermSingular || null,
            employeeTermPlural: employeeTermPlural || null,
            customSubscriptionEnabled: customSubscriptionEnabled,
            disableFreeAccounts: disableFreeAccounts,
            disableEnterpriseAccounts: disableEnterpriseAccounts,
            rootPage: rootPage,
            headingFontFamily: headingFontFamily || null,
            bodyFontFamily: bodyFontFamily || null,
            horizontal_logo_id: horizontalLogoId,
            horizontal_logo_dark_id: horizontalLogoDarkId,
            square_logo_id: squareLogoId,
            favicon_id: faviconId,
            onboarding_hero_image_id: onboardingHeroId,
            // SMTP settings
            smtpEnabled: smtpEnabled,
            notificationsEmail: notificationsEmail || null,
            smtpHost: smtpHost || null,
            smtpPort: smtpPort || null,
            smtpUsername: smtpUsername || null,
            smtpPassword: smtpPassword || null,
            smtpUseTls: smtpUseTls,
            smtpUseSsl: smtpUseSsl,
            // Welcome email customization
            welcomeEmailEnabled: welcomeEmailEnabled,
            welcomeEmailSubject: welcomeEmailSubject || null,
            welcomeEmailContent: welcomeEmailContent || null,
            // Onboarding completion customization
            onboardingCompletionContent: onboardingCompletionContent || null,
            // Onboarding team selection
            onboardingTeamSelection: onboardingTeamSelection,
            // Default public employees
            defaultPublicEmployeeIds: defaultPublicEmployeeIds.length > 0 ? defaultPublicEmployeeIds : null,
          },
          {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          }
        );
        showSnackbar("Whitelabel settings updated successfully", "success");
      } else {
        // Create new whitelabel
        await axios.post(
          `${hostname}/whitelabel`,
          {
            domain,
            settings: {
              ...settings,
              publicEmployeeFilter,
              publicFolderFilter,
              showEmployeeTemplates,
            },
            termsUrl: termsUrl || null,
            privacyUrl: privacyUrl || null,
            salesEmail: salesEmail || null,
            securityEmail: securityEmail || null,
            supportUrl: supportUrl || null,
            pricingUrl: pricingUrl || null,
            documentationUrl: documentationUrl || null,
            newCompanyWebhook: newCompanyWebhook || null,
            newCompanyListId: newCompanyListId || null,
            userImagePrompt: userImagePrompt || null,
            userImageModel: userImageModel || null,
            employeeTermSingular: employeeTermSingular || null,
            employeeTermPlural: employeeTermPlural || null,
            headingFontFamily: headingFontFamily || null,
            bodyFontFamily: bodyFontFamily || null,
            horizontal_logo_id: horizontalLogoId,
            horizontal_logo_dark_id: horizontalLogoDarkId,
            square_logo_id: squareLogoId,
            favicon_id: faviconId,
            onboarding_hero_image_id: onboardingHeroId,
            agreementAccepted: agreementAccepted,
            agreementVersion: "1.0",
            // SMTP settings
            smtpEnabled: smtpEnabled,
            notificationsEmail: notificationsEmail || null,
            smtpHost: smtpHost || null,
            smtpPort: smtpPort || null,
            smtpUsername: smtpUsername || null,
            smtpPassword: smtpPassword || null,
            smtpUseTls: smtpUseTls,
            smtpUseSsl: smtpUseSsl,
            // Welcome email customization
            welcomeEmailEnabled: welcomeEmailEnabled,
            welcomeEmailSubject: welcomeEmailSubject || null,
            welcomeEmailContent: welcomeEmailContent || null,
            // Onboarding completion customization
            onboardingCompletionContent: onboardingCompletionContent || null,
            // Onboarding team selection
            onboardingTeamSelection: onboardingTeamSelection,
            // Default public employees
            defaultPublicEmployeeIds: defaultPublicEmployeeIds.length > 0 ? defaultPublicEmployeeIds : null,
          },
          {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          }
        );
        showSnackbar("Whitelabel instance created successfully", "success");
      }

      setOriginalSettings(settings);

      // Close dialog and trigger refresh
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error("Error saving whitelabel settings:", error);

      // Use eventListener for error handling to show on top of dialogs
      if (eventListener) {
        // Check for specific limit error
        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          if (error.response.data.error.includes("limit")) {
            // Handle limit error specifically with contact info
            eventListener({
              type: "SET_ERROR",
              error: `You've reached your whitelabel limit. Please contact support for assistance.`,
            });
          } else {
            // Handle other API errors
            eventListener({
              type: "SET_ERROR",
              error: error.response.data.error,
            });
          }
        } else {
          // Handle general errors
          eventListener({
            type: "SET_ERROR",
            error: "Failed to save whitelabel settings",
          });
        }
      } else {
        // Fallback to snackbar if eventListener is not available
        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          if (error.response.data.error.includes("limit")) {
            showSnackbar(
              `You've reached your whitelabel limit. Please contact support for assistance.`,
              "error"
            );
          } else {
            showSnackbar(error.response.data.error, "error");
          }
        } else {
          showSnackbar("Failed to save whitelabel settings", "error");
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (
    field: keyof WhitelabelSettings,
    value: string
  ) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleColorChange = (
    field: keyof WhitelabelSettings,
    value: string
  ) => {
    // Remove # if present and add it back
    const cleanColor = value.replace("#", "");
    const formattedColor = cleanColor ? `#${cleanColor}` : "";

    setSettings((prev) => ({
      ...prev,
      [field]: formattedColor,
    }));
  };

  const handleReset = () => {
    setSettings(originalSettings);
  };

  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleLogoChange = (
    logoId: string | null,
    logoUrl: string | null,
    logoType: string
  ) => {
    // Debug: Log logo changes
    console.log("Logo change:", { logoType, logoId, logoUrl });

    // Update the appropriate logo state based on the type
    if (logoType === "horizontal") {
      setHorizontalLogoId(logoId);
      setHorizontalLogoUrl(logoUrl);
    } else if (logoType === "horizontal_dark") {
      setHorizontalLogoDarkId(logoId);
      setHorizontalLogoDarkUrl(logoUrl);
    } else if (logoType === "square") {
      setSquareLogoId(logoId);
      setSquareLogoUrl(logoUrl);
    } else if (logoType === "favicon") {
      setFaviconId(logoId);
      setFaviconUrl(logoUrl);
    } else if (logoType === "onboarding_hero") {
      setOnboardingHeroId(logoId);
      setOnboardingHeroUrl(logoUrl);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Load Stripe status when dialog opens and whitelabel exists
  useEffect(() => {
    if (open && whitelabel?.id) {
      loadStripeStatus();
    }
  }, [open, whitelabel?.id]);

  const loadStripeStatus = async () => {
    if (!whitelabel?.id) return;

    setStripeLoading(true);
    try {
      const currentToken = await getToken();
      const response = await axios.get(
        `${hostname}/whitelabel/${whitelabel.id}/stripe/status`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      setStripeStatus(response.data);
    } catch (error) {
      console.error("Error loading Stripe status:", error);
      setStripeStatus({ connected: false, status: "error" });
    } finally {
      setStripeLoading(false);
    }
  };

  const testSmtpSettings = async () => {
    if (!whitelabel?.id) {
      setSmtpTestResult({
        success: false,
        message: "Please save the whitelabel instance first",
      });
      return;
    }

    setSmtpTesting(true);
    setSmtpTestResult(null);
    try {
      const currentToken = await getToken();
      const response = await axios.post(
        `${hostname}/whitelabel/${whitelabel.id}/test-smtp`,
        {
          smtpHost,
          smtpPort,
          smtpUsername,
          smtpPassword,
          smtpUseTls,
          smtpUseSsl,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      setSmtpTestResult({
        success: response.data.success,
        message: response.data.message,
      });
    } catch (error: any) {
      console.error("Error testing SMTP:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to test SMTP settings";
      setSmtpTestResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setSmtpTesting(false);
    }
  };

  const createStripeAccount = async () => {
    if (!whitelabel?.id) return;

    setStripeLoading(true);
    try {
      const currentToken = await getToken();
      await axios.post(
        `${hostname}/whitelabel/${whitelabel.id}/stripe/account`,
        {},
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      // After creating account, create onboarding link
      const linkResponse = await axios.post(
        `${hostname}/whitelabel/${whitelabel.id}/stripe/account-link`,
        {},
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      // Open Stripe onboarding in new window
      const safeUrlPattern = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;
      if (safeUrlPattern.test(linkResponse.data.url)) window.open(linkResponse.data.url, "_blank");
      showSnackbar(
        "Stripe account created! Complete onboarding in the new window.",
        "success"
      );

      // Reload status after a short delay
      setTimeout(() => {
        loadStripeStatus();
      }, 2000);
    } catch (error: any) {
      console.error("Error creating Stripe account:", error);
      const errorMessage =
        error.response?.data?.error || "Failed to create Stripe account";
      if (eventListener) {
        eventListener({ type: "SET_ERROR", error: errorMessage });
      } else {
        showSnackbar(errorMessage, "error");
      }
    } finally {
      setStripeLoading(false);
    }
  };

  const refreshStripeOnboarding = async () => {
    if (!whitelabel?.id) return;

    setStripeLoading(true);
    try {
      const currentToken = await getToken();
      const linkResponse = await axios.post(
        `${hostname}/whitelabel/${whitelabel.id}/stripe/account-link`,
        {},
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      // Open Stripe onboarding in new window
      const safeUrlPattern2 = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;
      if (safeUrlPattern2.test(linkResponse.data.url)) window.open(linkResponse.data.url, "_blank");
      showSnackbar("Stripe onboarding opened in new window", "success");
    } catch (error: any) {
      console.error("Error refreshing Stripe onboarding:", error);
      const errorMessage =
        error.response?.data?.error || "Failed to refresh Stripe onboarding";
      if (eventListener) {
        eventListener({ type: "SET_ERROR", error: errorMessage });
      } else {
        showSnackbar(errorMessage, "error");
      }
    } finally {
      setStripeLoading(false);
    }
  };

  const disconnectStripeAccount = async () => {
    if (!whitelabel?.id) return;

    setStripeLoading(true);
    try {
      const currentToken = await getToken();
      await axios.post(
        `${hostname}/whitelabel/${whitelabel.id}/stripe/disconnect`,
        {},
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      setStripeStatus({ connected: false, status: "not_connected" });
      showSnackbar("Stripe account disconnected successfully", "success");
    } catch (error: any) {
      console.error("Error disconnecting Stripe account:", error);
      const errorMessage =
        error.response?.data?.error || "Failed to disconnect Stripe account";
      if (eventListener) {
        eventListener({ type: "SET_ERROR", error: errorMessage });
      } else {
        showSnackbar(errorMessage, "error");
      }
    } finally {
      setStripeLoading(false);
    }
  };

  const getStripeStatusColor = (status: string) => {
    switch (status) {
      case "enabled":
        return "success";
      case "pending":
        return "warning";
      case "not_connected":
        return "default";
      default:
        return "error";
    }
  };

  const getStripeStatusText = (status: string) => {
    switch (status) {
      case "enabled":
        return "Connected & Ready";
      case "pending":
        return "Pending Setup";
      case "not_connected":
        return "Not Connected";
      default:
        return "Error";
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {whitelabel ? "Edit" : "Create"} White-Label Instance{" "}
          {whitelabel && !whitelabel.is_owner && "(Shared)"}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 0,
            height: "600px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="whitelabel settings tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="General" id="tab-0" aria-controls="tabpanel-0" />
              <Tab label="Logos" id="tab-1" aria-controls="tabpanel-1" />
              <Tab label="Branding" id="tab-2" aria-controls="tabpanel-2" />
              <Tab label="Email" id="tab-3" aria-controls="tabpanel-3" />
              <Tab label="Onboarding" id="tab-4" aria-controls="tabpanel-4" />
              <Tab label="Employees" id="tab-5" aria-controls="tabpanel-5" />
              <Tab label="Payments" id="tab-6" aria-controls="tabpanel-6" />
            </Tabs>
          </Box>
          <Box sx={{ flex: 1, overflow: "auto" }}>
            {/* General Tab */}
            {activeTab === 0 && (
              <Box
                sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Configure basic settings and domain information
                </Typography>

                <TextField
                  label="Domain"
                  placeholder="app.example.com, ai.example.com, or example.com"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  fullWidth
                  required
                  disabled={!isOwner}
                  helperText="Enter your custom domain (subdomain recommended). Examples: app.example.com, ai.example.com, portal.company.com. After saving, use DNS Setup to configure your domain."
                />

                <TextField
                  label="Title"
                  value={settings.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  fullWidth
                  helperText="The main title shown in the application"
                />

                <TextField
                  label="Page Title"
                  value={settings.headTitle}
                  onChange={(e) =>
                    handleInputChange("headTitle", e.target.value)
                  }
                  fullWidth
                  helperText="The title shown in the browser tab"
                />

                <TextField
                  label="Favicon Text"
                  value={settings.faviconText}
                  onChange={(e) =>
                    handleInputChange("faviconText", e.target.value)
                  }
                  fullWidth
                  inputProps={{ maxLength: 2 }}
                  helperText="1-2 characters shown as the default favicon if no image is uploaded"
                />

                <TextField
                  label="Terms of Service URL"
                  value={termsUrl}
                  onChange={(e) => setTermsUrl(e.target.value)}
                  fullWidth
                  placeholder="https://yourcompany.com/terms"
                  helperText="External URL to redirect users to when they click Terms of Service links. Leave empty to show the platform's built-in terms page. Do not link back to your own /terms page as this will cause a redirect loop."
                />

                <TextField
                  label="Privacy Policy URL"
                  value={privacyUrl}
                  onChange={(e) => setPrivacyUrl(e.target.value)}
                  fullWidth
                  placeholder="https://yourcompany.com/privacy"
                  helperText="External URL to redirect users to when they click Privacy Policy links. Leave empty to show the platform's built-in privacy page. Do not link back to your own /privacy page as this will cause a redirect loop."
                />

                <TextField
                  label="Sales Email"
                  value={salesEmail}
                  onChange={(e) => setSalesEmail(e.target.value)}
                  fullWidth
                  placeholder="sales@yourcompany.com"
                  type="email"
                  error={salesEmail !== "" && !validateEmail(salesEmail)}
                  helperText={
                    salesEmail !== "" && !validateEmail(salesEmail)
                      ? "Please enter a valid email address"
                      : "Custom sales email for contact sales links (optional). If not provided, defaults to sales@yourdomain.com."
                  }
                />

                <TextField
                  label="Security Email"
                  value={securityEmail}
                  onChange={(e) => setSecurityEmail(e.target.value)}
                  fullWidth
                  placeholder="security@yourcompany.com"
                  type="email"
                  error={securityEmail !== "" && !validateEmail(securityEmail)}
                  helperText={
                    securityEmail !== "" && !validateEmail(securityEmail)
                      ? "Please enter a valid email address"
                      : "Custom security contact email used on security pages (optional)."
                  }
                />

                <TextField
                  label="Support URL"
                  value={supportUrl}
                  onChange={(e) => setSupportUrl(e.target.value)}
                  fullWidth
                  placeholder="mailto:support@yourcompany.com or https://yourcompany.com/support"
                  helperText="Custom support URL or email (optional). Use mailto:your-email@company.com for email links, or https://... for web pages like Discord/help centers. If left blank, the built-in support ticket system will be used, allowing platform support to see and acknowledge any platform bugs or issues."
                />

                <TextField
                  label="Pricing URL"
                  value={pricingUrl}
                  onChange={(e) => setPricingUrl(e.target.value)}
                  fullWidth
                  placeholder="https://yourcompany.com/pricing"
                  helperText="Custom pricing page URL (optional). If not provided, defaults to built-in pricing page."
                />

                <TextField
                  label="Documentation URL"
                  value={documentationUrl}
                  onChange={(e) => setDocumentationUrl(e.target.value)}
                  fullWidth
                  placeholder="https://yourcompany.com/docs"
                  helperText="Custom documentation page URL (optional). If not provided, uses the built-in documentation."
                />

                <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                  New Company Actions
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Configure actions that occur when new companies are created
                  through onboarding.
                </Typography>

                <TextField
                  label="New Company Webhook URL"
                  value={newCompanyWebhook}
                  onChange={(e) => setNewCompanyWebhook(e.target.value)}
                  fullWidth
                  placeholder="https://yourapi.com/webhook/new-company"
                  helperText="Webhook URL to call when a new company is created (optional). Will receive POST with company data."
                />

                <TextField
                  label="New Company List ID"
                  value={newCompanyListId}
                  onChange={(e) => setNewCompanyListId(e.target.value)}
                  fullWidth
                  placeholder="list-uuid-here"
                  helperText="List ID to add new company leads to (optional). Company owner will be added as a list member."
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  Root Page Configuration
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Choose which page visitors see when they first arrive at your
                  domain.
                </Typography>
                <FormControl fullWidth>
                  <InputLabel id="root-page-label">Root Page</InputLabel>
                  <Select
                    labelId="root-page-label"
                    value={rootPage}
                    label="Root Page"
                    onChange={(e) => setRootPage(e.target.value as string)}
                  >
                    <MenuItem value="login">
                      Login Page (default) - Shows login/signup form
                    </MenuItem>
                    <MenuItem value="home">
                      Home Page - Shows marketing landing page with features
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Logos Tab */}
            {activeTab === 1 && (
              <Box sx={{ p: 3 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Upload custom logos for your whitelabel instance
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <LogoUploader
                      title="Horizontal Logo (Light Mode)"
                      description="Main logo used in headers and branding"
                      logoType="horizontal"
                      logoId={horizontalLogoId}
                      logoUrl={horizontalLogoUrl}
                      whitelabelId={whitelabel?.id}
                      getToken={getToken}
                      onLogoChange={handleLogoChange}
                      recommended="1000x200px"
                      aspectRatio={5}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <LogoUploader
                      title="Horizontal Logo (Dark Mode)"
                      description="Main logo for dark mode"
                      logoType="horizontal_dark"
                      logoId={horizontalLogoDarkId}
                      logoUrl={horizontalLogoDarkUrl}
                      whitelabelId={whitelabel?.id}
                      getToken={getToken}
                      onLogoChange={handleLogoChange}
                      recommended="1000x200px"
                      aspectRatio={5}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <LogoUploader
                      title="Square Logo"
                      description="Used in mobile and constrained spaces"
                      logoType="square"
                      logoId={squareLogoId}
                      logoUrl={squareLogoUrl}
                      whitelabelId={whitelabel?.id}
                      getToken={getToken}
                      onLogoChange={handleLogoChange}
                      recommended="512x512px"
                      aspectRatio={1}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <LogoUploader
                      title="Favicon"
                      description="Icon shown in browser tabs"
                      logoType="favicon"
                      logoId={faviconId}
                      logoUrl={faviconUrl}
                      whitelabelId={whitelabel?.id}
                      getToken={getToken}
                      onLogoChange={handleLogoChange}
                      recommended="32x32px"
                      aspectRatio={1}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <LogoUploader
                      title="Onboarding Hero"
                      description="Background image shown during user onboarding"
                      logoType="onboarding_hero"
                      logoId={onboardingHeroId}
                      logoUrl={onboardingHeroUrl}
                      whitelabelId={whitelabel?.id}
                      getToken={getToken}
                      onLogoChange={handleLogoChange}
                      recommended="1920x1080px"
                      aspectRatio={16 / 9}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Branding Tab */}
            {activeTab === 2 && (
              <Box
                sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Customize colors and visual branding for light and dark modes
                </Typography>

                {/* Light Mode Colors */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "action.hover",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    Light Mode Colors
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" gutterBottom>
                        Primary Color
                      </Typography>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <Box
                          component="input"
                          type="color"
                          value={settings.primaryColor}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleColorChange("primaryColor", e.target.value)
                          }
                          sx={{
                            width: 60,
                            height: 40,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            cursor: "pointer",
                          }}
                        />
                        <TextField
                          value={settings.primaryColor}
                          onChange={(e) =>
                            handleColorChange("primaryColor", e.target.value)
                          }
                          fullWidth
                          size="small"
                          placeholder="#000000"
                          helperText="Main brand color for light mode"
                        />
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" gutterBottom>
                        Secondary Color
                      </Typography>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <Box
                          component="input"
                          type="color"
                          value={settings.secondaryColor}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleColorChange("secondaryColor", e.target.value)
                          }
                          sx={{
                            width: 60,
                            height: 40,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            cursor: "pointer",
                          }}
                        />
                        <TextField
                          value={settings.secondaryColor}
                          onChange={(e) =>
                            handleColorChange("secondaryColor", e.target.value)
                          }
                          fullWidth
                          size="small"
                          placeholder="#000000"
                          helperText="Secondary brand color for light mode"
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Dark Mode Colors - intentionally uses dark background for preview */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#1a1a1a",
                    border: "1px solid",
                    borderColor: "#333",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "#fff",
                    }}
                  >
                    Dark Mode Colors
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        gutterBottom
                        sx={{ color: "#ccc" }}
                      >
                        Primary Color
                      </Typography>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <Box
                          component="input"
                          type="color"
                          value={settings.primaryColorDark || settings.primaryColor}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleColorChange("primaryColorDark", e.target.value)
                          }
                          sx={{
                            width: 60,
                            height: 40,
                            border: "1px solid #555",
                            borderRadius: 1,
                            cursor: "pointer",
                          }}
                        />
                        <TextField
                          value={settings.primaryColorDark || ""}
                          onChange={(e) =>
                            handleColorChange("primaryColorDark", e.target.value)
                          }
                          fullWidth
                          size="small"
                          placeholder={settings.primaryColor || "#A3F7BF"}
                          helperText="Main brand color for dark mode"
                          sx={{
                            "& .MuiInputBase-root": {
                              backgroundColor: "#2a2a2a",
                              color: "#fff",
                            },
                            "& .MuiFormHelperText-root": {
                              color: "#888",
                            },
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        gutterBottom
                        sx={{ color: "#ccc" }}
                      >
                        Secondary Color
                      </Typography>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <Box
                          component="input"
                          type="color"
                          value={settings.secondaryColorDark || settings.secondaryColor}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleColorChange("secondaryColorDark", e.target.value)
                          }
                          sx={{
                            width: 60,
                            height: 40,
                            border: "1px solid #555",
                            borderRadius: 1,
                            cursor: "pointer",
                          }}
                        />
                        <TextField
                          value={settings.secondaryColorDark || ""}
                          onChange={(e) =>
                            handleColorChange("secondaryColorDark", e.target.value)
                          }
                          fullWidth
                          size="small"
                          placeholder={settings.secondaryColor || "#ffffff"}
                          helperText="Secondary brand color for dark mode"
                          sx={{
                            "& .MuiInputBase-root": {
                              backgroundColor: "#2a2a2a",
                              color: "#fff",
                            },
                            "& .MuiFormHelperText-root": {
                              color: "#888",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" gutterBottom>
                  Typography
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Customize the fonts used throughout your whitelabel instance
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Autocomplete
                    loading={fontsLoading}
                    options={FONT_OPTIONS}
                    groupBy={(option) => option.category || ""}
                    getOptionLabel={(option) => option?.label || ""}
                    value={
                      FONT_OPTIONS.find((f) => f.value === headingFontFamily) ||
                      null
                    }
                    onChange={(_, newValue) => {
                      setHeadingFontFamily(newValue?.value || "");
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option?.value === value?.value
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Heading Font"
                        placeholder="Select a font..."
                        helperText={
                          fontsLoading
                            ? "Loading fonts..."
                            : `${FONT_OPTIONS.length - 1} fonts available`
                        }
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.value || "default"}>
                        <span style={{ fontFamily: option.value || "inherit" }}>
                          {option.label}
                        </span>
                      </li>
                    )}
                  />

                  <Autocomplete
                    loading={fontsLoading}
                    options={FONT_OPTIONS}
                    groupBy={(option) => option.category || ""}
                    getOptionLabel={(option) => option?.label || ""}
                    value={
                      FONT_OPTIONS.find((f) => f.value === bodyFontFamily) ||
                      null
                    }
                    onChange={(_, newValue) => {
                      setBodyFontFamily(newValue?.value || "");
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option?.value === value?.value
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Body Font"
                        placeholder="Select a font..."
                        helperText={
                          fontsLoading
                            ? "Loading fonts..."
                            : `${FONT_OPTIONS.length - 1} fonts available`
                        }
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.value || "default"}>
                        <span style={{ fontFamily: option.value || "inherit" }}>
                          {option.label}
                        </span>
                      </li>
                    )}
                  />

                  {(headingFontFamily || bodyFontFamily) && (
                    <Box
                      sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Preview
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          fontFamily: headingFontFamily
                            ? `'${headingFontFamily}', serif`
                            : "Cardo, serif",
                          mb: 1,
                        }}
                      >
                        Sample Heading Text
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontFamily: bodyFontFamily
                            ? `'${bodyFontFamily}', sans-serif`
                            : "Inter, Arial, sans-serif",
                        }}
                      >
                        This is sample body text to preview your selected font.
                        The quick brown fox jumps over the lazy dog.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Email Tab */}
            {activeTab === 3 && (
              <Box
                sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Configure email sending settings for notifications from your
                  whitelabel instance
                </Typography>

                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  SMTP Settings
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Configure your own SMTP server to send emails from your email
                  address. If not enabled, emails will be sent via our default
                  email service (requires DNS configuration).
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={smtpEnabled}
                      onChange={(e) => setSmtpEnabled(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">
                        Enable Custom SMTP
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Use your own SMTP server instead of our default email
                        service
                      </Typography>
                    </Box>
                  }
                />

                {smtpEnabled && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      mt: 2,
                      p: 2,
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <TextField
                      label="From Email Address"
                      value={notificationsEmail}
                      onChange={(e) => setNotificationsEmail(e.target.value)}
                      fullWidth
                      required
                      placeholder="noreply@yourcompany.com"
                      type="email"
                      helperText="The email address notifications will be sent from (must be authorized by your SMTP provider)"
                    />

                    <Divider />

                    <TextField
                      label="SMTP Host"
                      value={smtpHost}
                      onChange={(e) => {
                        setSmtpHost(e.target.value);
                        setSmtpTestResult(null);
                      }}
                      fullWidth
                      required
                      placeholder="smtp.yourcompany.com"
                      helperText="Your SMTP server hostname (e.g., smtp.gmail.com, smtp.sendgrid.net)"
                    />

                    <TextField
                      label="SMTP Port"
                      value={smtpPort}
                      onChange={(e) => {
                        setSmtpPort(
                          e.target.value ? parseInt(e.target.value) : ""
                        );
                        setSmtpTestResult(null);
                      }}
                      fullWidth
                      type="number"
                      placeholder="587"
                      helperText="Common ports: 587 (TLS/STARTTLS), 465 (SSL), 25 (unencrypted - not recommended)"
                    />

                    <TextField
                      label="SMTP Username"
                      value={smtpUsername}
                      onChange={(e) => {
                        setSmtpUsername(e.target.value);
                        setSmtpTestResult(null);
                      }}
                      fullWidth
                      placeholder="your-username or email@yourcompany.com"
                      helperText="Authentication username for your SMTP server"
                    />

                    <TextField
                      label="SMTP Password"
                      value={smtpPassword}
                      onChange={(e) => {
                        setSmtpPassword(e.target.value);
                        setSmtpTestResult(null);
                      }}
                      fullWidth
                      type="password"
                      placeholder="Your SMTP password or app-specific password"
                      helperText="Authentication password for your SMTP server (stored securely)"
                    />

                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={smtpUseTls}
                            onChange={(e) => {
                              setSmtpUseTls(e.target.checked);
                              if (e.target.checked) setSmtpUseSsl(false);
                              setSmtpTestResult(null);
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">
                              Use TLS (STARTTLS)
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Recommended for port 587
                            </Typography>
                          </Box>
                        }
                      />

                      <FormControlLabel
                        control={
                          <Switch
                            checked={smtpUseSsl}
                            onChange={(e) => {
                              setSmtpUseSsl(e.target.checked);
                              if (e.target.checked) setSmtpUseTls(false);
                              setSmtpTestResult(null);
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">
                              Use SSL (Implicit TLS)
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Required for port 465
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>

                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>Common SMTP Providers:</strong>
                        <br />
                        • Gmail/Google Workspace: smtp.gmail.com:587 (TLS),
                        requires app password
                        <br />
                        • SendGrid: smtp.sendgrid.net:587 (TLS)
                        <br />
                        • Mailgun: smtp.mailgun.org:587 (TLS)
                        <br />• Amazon SES: email-smtp.{"{region}"}
                        .amazonaws.com:587 (TLS)
                      </Typography>
                    </Alert>

                    {smtpTestResult && (
                      <Alert
                        severity={smtpTestResult.success ? "success" : "error"}
                        sx={{ mt: 2 }}
                        onClose={() => setSmtpTestResult(null)}
                      >
                        {smtpTestResult.message}
                      </Alert>
                    )}

                    <Box sx={{ mt: 2 }}>
                      <LoadingButton
                        variant="outlined"
                        onClick={testSmtpSettings}
                        loading={smtpTesting}
                        disabled={!smtpHost || !smtpUsername || !smtpPassword}
                      >
                        Test Connection
                      </LoadingButton>
                      {!whitelabel?.id && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 2 }}
                        >
                          Save first to test
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Welcome Email Settings */}
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Welcome Email
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Customize or disable the welcome email sent to new users after
                  they complete onboarding.
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={welcomeEmailEnabled}
                      onChange={(e) => setWelcomeEmailEnabled(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">
                        Send Welcome Email
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Send an email to new users after they complete
                        onboarding
                      </Typography>
                    </Box>
                  }
                />

                {welcomeEmailEnabled && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      mt: 2,
                      p: 2,
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <TextField
                      label="Email Subject"
                      value={welcomeEmailSubject}
                      onChange={(e) => setWelcomeEmailSubject(e.target.value)}
                      fullWidth
                      placeholder="Welcome to your AI-powered workspace!"
                      helperText="Leave blank to use the default subject"
                    />

                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Email Content
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Use the editor below to create your custom welcome email. Leave blank to use the default content.
                      </Typography>
                      <EditorProvider>
                        <RswEditor
                          style={{ minHeight: 250 }}
                          containerProps={{ style: { resize: "vertical" } }}
                          value={welcomeEmailContent}
                          onChange={(e) => setWelcomeEmailContent(e.target.value)}
                        />
                      </EditorProvider>
                    </Box>

                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>Available placeholders:</strong>
                      </Typography>
                      <Typography variant="body2" component="ul" sx={{ pl: 2, mt: 0.5 }}>
                        <li><code>{"{{firstName}}"}</code> - User's first name</li>
                        <li><code>{"{{companyName}}"}</code> - Company name</li>
                      </Typography>
                    </Alert>
                  </Box>
                )}
              </Box>
            )}

            {/* Onboarding Tab */}
            {activeTab === 4 && (
              <Box
                sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Customize the onboarding experience for new users
                </Typography>

                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Team Selection
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Configure how new users build their AI team during onboarding.
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={onboardingTeamSelection}
                      onChange={(e) => setOnboardingTeamSelection(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">
                        Enable Team Selection
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        When enabled, users will pick their AI team from pre-built templates during onboarding.
                        When disabled, employees will be auto-generated based on company info.
                      </Typography>
                    </Box>
                  }
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Completion Page Content
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Customize the content displayed on the final page of the
                  onboarding flow. Use the editor below to create rich content.
                  Leave blank to use the default content.
                </Typography>

                <EditorProvider>
                  <RswEditor
                    style={{ minHeight: 350 }}
                    containerProps={{ style: { resize: "vertical" } }}
                    value={onboardingCompletionContent}
                    onChange={(e) => setOnboardingCompletionContent(e.target.value)}
                  />
                </EditorProvider>

                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Available placeholders:</strong>
                  </Typography>
                  <Typography variant="body2" component="ul" sx={{ pl: 2, mt: 0.5 }}>
                    <li><code>{"{{appName}}"}</code> - Your app/brand name</li>
                    <li><code>{"{{companyName}}"}</code> - User's company name</li>
                  </Typography>
                </Alert>
              </Box>
            )}

            {/* Employees Tab */}
            {activeTab === 5 && (
              <Box
                sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Configure employee-related settings including terminology and
                  image generation
                </Typography>

                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Employee Creation
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Configure how users create new employees in your whitelabel
                  instance.
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={showEmployeeTemplates}
                      onChange={(e) => setShowEmployeeTemplates(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">
                        Show Pre-Built Templates
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        When enabled, users will see a dialog offering pre-built
                        employee templates when creating a new employee. When
                        disabled, users will go directly to the create from
                        scratch form.
                      </Typography>
                    </Box>
                  }
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Default Employees for New Companies
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Select public employees that will be automatically added to new
                  companies created in this whitelabel. When configured, these
                  employees replace the default AI-generated employees during
                  onboarding.
                </Typography>

                {/* Selected Default Employees List */}
                {selectedDefaultEmployees.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Selected Default Employees ({selectedDefaultEmployees.length})
                    </Typography>
                    <List dense sx={{ bgcolor: "action.hover", borderRadius: 1 }}>
                      {selectedDefaultEmployees.map((employee) => (
                        <ListItem key={employee.id}>
                          <ListItemAvatar>
                            <Avatar src={employee.picture} alt={employee.name}>
                              {employee.name?.charAt(0) || "E"}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={employee.name}
                            secondary={
                              <>
                                {employee.title}
                                {employee.ownerCompany && (
                                  <> - {employee.ownerCompany.name}</>
                                )}
                              </>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => {
                                const newIds = defaultPublicEmployeeIds.filter(
                                  (id) => id !== employee.id
                                );
                                setDefaultPublicEmployeeIds(newIds);
                                setSelectedDefaultEmployees(
                                  selectedDefaultEmployees.filter(
                                    (e) => e.id !== employee.id
                                  )
                                );
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Add Public Employee Selector */}
                <Autocomplete
                  options={availablePublicEmployees.filter(
                    (emp) => !defaultPublicEmployeeIds.includes(emp.id)
                  )}
                  getOptionLabel={(option) =>
                    `${option.name} - ${option.title}${option.ownerCompany ? ` (${option.ownerCompany.name})` : ""}`
                  }
                  loading={publicEmployeesLoading}
                  filterOptions={(x) => x} // Disable client-side filtering, we filter server-side
                  onOpen={() => {
                    // Refresh list when opening dropdown
                    if (availablePublicEmployees.length === 0) {
                      fetchPublicEmployees();
                    }
                  }}
                  onInputChange={(_, value, reason) => {
                    if (reason === "input") {
                      setPublicEmployeeSearch(value);
                      // Debounce search - fetch after typing stops
                      fetchPublicEmployees(value, 1, false);
                    } else if (reason === "clear") {
                      setPublicEmployeeSearch("");
                      fetchPublicEmployees("", 1, false);
                    }
                  }}
                  onChange={(_, value) => {
                    if (value) {
                      setDefaultPublicEmployeeIds([
                        ...defaultPublicEmployeeIds,
                        value.id,
                      ]);
                      setSelectedDefaultEmployees([
                        ...selectedDefaultEmployees,
                        value,
                      ]);
                    }
                  }}
                  ListboxProps={{
                    onScroll: (event: React.SyntheticEvent) => {
                      const listboxNode = event.currentTarget;
                      if (
                        listboxNode.scrollTop + listboxNode.clientHeight >=
                        listboxNode.scrollHeight - 50
                      ) {
                        handleLoadMorePublicEmployees();
                      }
                    },
                    style: { maxHeight: 300 },
                  }}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      {...props}
                      key={option.id}
                    >
                      <Avatar
                        src={option.picture}
                        alt={option.name}
                        sx={{ width: 32, height: 32 }}
                      >
                        {option.name?.charAt(0) || "E"}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.title}
                          {option.ownerCompany && ` - ${option.ownerCompany.name}`}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add Public Employee"
                      placeholder="Search or scroll to browse..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {publicEmployeesLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  value={null}
                  blurOnSelect
                  clearOnBlur
                  noOptionsText={publicEmployeesLoading ? "Loading..." : "No public employees found"}
                />

                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    When default employees are set, new companies will
                    automatically have these public employees added instead of
                    generating 3 random AI employees. Users can still add more
                    employees after onboarding.
                  </Typography>
                </Alert>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Terminology Customization
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Customize the terminology used throughout the platform. Leave
                  blank to use default "Employee/Employees".
                </Typography>

                <TextField
                  label="Employee Term (Singular)"
                  value={employeeTermSingular}
                  onChange={(e) => setEmployeeTermSingular(e.target.value)}
                  fullWidth
                  placeholder="Employee"
                  helperText='Custom singular term for "Employee" (e.g., "Agent", "Assistant", "Bot"). Leave blank for default.'
                />

                <TextField
                  label="Employee Term (Plural)"
                  value={employeeTermPlural}
                  onChange={(e) => setEmployeeTermPlural(e.target.value)}
                  fullWidth
                  placeholder="Employees"
                  helperText='Custom plural term for "Employees" (e.g., "Agents", "Assistants", "Bots"). Leave blank for default.'
                />

                <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                  Public Employee Settings
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Configure which public employees are visible to companies in
                  this whitelabel instance.
                </Typography>

                <FormControl fullWidth>
                  <InputLabel id="public-employee-filter-label">
                    Public Employees
                  </InputLabel>
                  <Select
                    labelId="public-employee-filter-label"
                    value={publicEmployeeFilter}
                    label="Public Employees"
                    onChange={(e) => setPublicEmployeeFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Public Employees</MenuItem>
                    <MenuItem value="whitelabel_only">
                      Whitelabel-Only Employees
                    </MenuItem>
                    <MenuItem value="none">Disabled</MenuItem>
                  </Select>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {publicEmployeeFilter === "none"
                      ? "Public employees are disabled. Companies cannot browse or make employees public."
                      : publicEmployeeFilter === "all"
                      ? "Companies can see all public employees from any whitelabel instance."
                      : "Companies can only see public employees created within this whitelabel instance."}
                  </Typography>
                </FormControl>

                <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                  Public Folder Access
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Configure which public folders are visible to companies in this whitelabel instance.
                </Typography>

                <FormControl fullWidth>
                  <InputLabel id="public-folder-filter-label">
                    Public Folders
                  </InputLabel>
                  <Select
                    labelId="public-folder-filter-label"
                    value={publicFolderFilter}
                    label="Public Folders"
                    onChange={(e) => setPublicFolderFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Public Folders</MenuItem>
                    <MenuItem value="whitelabel_only">Whitelabel-Only Folders</MenuItem>
                    <MenuItem value="none">Disabled</MenuItem>
                  </Select>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {publicFolderFilter === "none"
                      ? "Public folders are disabled. Companies cannot browse or share folders publicly."
                      : publicFolderFilter === "all"
                      ? "Companies can see all public folders from any whitelabel instance."
                      : "Companies can only see public folders created within this whitelabel instance."
                    }
                  </Typography>
                </FormControl>

                <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                  Employee Image Generation
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Configure the prompt used to generate employee profile images.
                  Available placeholders: <strong>{"{{name}}"}</strong>,{" "}
                  <strong>{"{{title}}"}</strong>,{" "}
                  <strong>{"{{companyName}}"}</strong>
                </Typography>

                <TextField
                  label="Employee Image Prompt Template"
                  value={userImagePrompt}
                  onChange={(e) => setUserImagePrompt(e.target.value)}
                  fullWidth
                  multiline
                  rows={6}
                  helperText="Custom prompt template for generating employee profile images. Use the placeholders above to dynamically insert employee data."
                  sx={{ mb: 3 }}
                />

                <FormControl fullWidth>
                  <InputLabel id="user-image-model-label">
                    Image Generation Model
                  </InputLabel>
                  <Select
                    labelId="user-image-model-label"
                    value={userImageModel}
                    label="Image Generation Model"
                    onChange={(e) => setUserImageModel(e.target.value)}
                  >
                    {imageModels.map((imageModel, index) => (
                      <MenuItem
                        value={imageModel.id}
                        key={index}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>{imageModel.name}</span>
                        <Chip
                          label={`${imageModel.credits || 5} credits`}
                          size="small"
                          color="primary"
                          sx={{ ml: 1 }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Select the AI model to use for generating employee profile
                    images. Different models produce different artistic styles.
                  </Typography>
                </FormControl>
              </Box>
            )}

            {/* Payments Tab */}
            {activeTab === 6 && (
              <Box
                sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}
              >
                {/* Subscription Settings Section */}
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Subscription Settings
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Configure how subscriptions are handled for this whitelabel
                    instance.
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={customSubscriptionEnabled}
                          onChange={(e) =>
                            setCustomSubscriptionEnabled(e.target.checked)
                          }
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">
                            Custom Subscription Handling
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Hide the standard subscription menu and replace it
                            with a mailto link to your sales email for manual
                            quote handling.
                          </Typography>
                        </Box>
                      }
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={disableFreeAccounts}
                          onChange={(e) =>
                            setDisableFreeAccounts(e.target.checked)
                          }
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">
                            Disable Free Subscriptions
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            When enabled, users must purchase a paid
                            subscription plan before they can complete
                            onboarding and access the platform. The subscription
                            dialog will appear immediately after login and
                            cannot be closed until a paid plan is selected. Free
                            tier access is completely disabled for this
                            whitelabel instance.
                          </Typography>
                        </Box>
                      }
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={disableEnterpriseAccounts}
                          onChange={(e) =>
                            setDisableEnterpriseAccounts(e.target.checked)
                          }
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">
                            Disable Enterprise Tier
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            When enabled, the Enterprise subscription tier will
                            be hidden from the subscription options. Users will
                            only see Free, Entrepreneur, and Business tiers.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="h6" gutterBottom>
                    Stripe Connect
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Connect your Stripe account to receive payments directly
                    when customers subscribe to your whitelabel pricing.
                  </Typography>
                </Box>

                {stripeLoading ? (
                  <Typography color="text.secondary">
                    Loading Stripe status...
                  </Typography>
                ) : (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="body1">Status:</Typography>
                      <Chip
                        label={getStripeStatusText(
                          stripeStatus?.status || "not_connected"
                        )}
                        color={
                          getStripeStatusColor(
                            stripeStatus?.status || "not_connected"
                          ) as any
                        }
                        size="small"
                      />
                      {stripeStatus?.account_id && (
                        <Typography variant="body2" color="text.secondary">
                          Account: {stripeStatus.account_id.slice(0, 12)}...
                        </Typography>
                      )}
                    </Box>

                    {stripeStatus?.connected ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {stripeStatus.status === "pending" && (
                          <Box>
                            <Typography
                              variant="body2"
                              color="warning.main"
                              sx={{ mb: 1 }}
                            >
                              Your Stripe account setup is incomplete.
                            </Typography>
                            {stripeStatus.requirements && (
                              <Box sx={{ ml: 2 }}>
                                {stripeStatus.requirements.currently_due
                                  ?.length > 0 && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Required:{" "}
                                    {stripeStatus.requirements.currently_due.join(
                                      ", "
                                    )}
                                  </Typography>
                                )}
                              </Box>
                            )}
                            <Button
                              variant="contained"
                              color="warning"
                              onClick={refreshStripeOnboarding}
                              disabled={stripeLoading}
                              sx={{ mt: 1 }}
                            >
                              Complete Setup
                            </Button>
                          </Box>
                        )}

                        {stripeStatus.status === "enabled" && (
                          <Box>
                            <Typography
                              variant="body2"
                              color="success.main"
                              sx={{ mb: 1 }}
                            >
                              ✓ Your Stripe account is fully connected and ready
                              to receive payments.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Charges enabled:{" "}
                              {stripeStatus.charges_enabled ? "Yes" : "No"}
                              <br />
                              Details submitted:{" "}
                              {stripeStatus.details_submitted ? "Yes" : "No"}
                            </Typography>
                          </Box>
                        )}

                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Button
                            variant="outlined"
                            onClick={refreshStripeOnboarding}
                            disabled={stripeLoading}
                          >
                            Manage Account
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={disconnectStripeAccount}
                            disabled={stripeLoading}
                          >
                            Disconnect
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          Connect your Stripe account to start receiving
                          payments directly from your whitelabel customers.
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={createStripeAccount}
                          disabled={stripeLoading || !whitelabel?.id}
                        >
                          Connect Stripe Account
                        </Button>
                      </Box>
                    )}

                    {!whitelabel?.id && (
                      <Typography variant="body2" color="warning.main">
                        Save your whitelabel instance first before connecting
                        Stripe.
                      </Typography>
                    )}
                  </Box>
                )}

                <Divider />

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    <strong>How it works:</strong>
                    <br />
                    1. Connect your Stripe account above
                    <br />
                    2. When customers subscribe through your whitelabel domain,
                    payments go directly to your Stripe account
                    <br />
                    3. Our platform fee is automatically deducted from each
                    payment
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReset} disabled={loading || saving}>
            Reset
          </Button>
          <Button onClick={onClose} disabled={loading || saving}>
            Cancel
          </Button>
          <LoadingButton
            onClick={handleSave}
            variant="contained"
            loading={saving}
            disabled={loading || !isOwner}
          >
            {isOwner ? "Save Changes" : "Read Only"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <WhitelabelAgreementDialog
        open={agreementDialogOpen}
        onAccept={() => {
          setAgreementAccepted(true);
          setAgreementDialogOpen(false);
          // Retry save after agreement is accepted
          handleSave();
        }}
        onDecline={() => {
          setAgreementDialogOpen(false);
          setAgreementAccepted(false);
        }}
      />
    </>
  );
}
